import { BffApiClientMolecule } from "@/app/api-client/store"
import { BlockchainFiltersMoleculeReatom } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import { LeaderboardPageType } from "@/app/navigation/leaderboard-types"
import { priceView } from "@/app/price"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { UnionAddress } from "@anaconda/core/blockchain/address"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import {
  type CollectionSearchSort,
  CollectionSearchSortDirection,
} from "@anaconda/core/nft-collection/search/types"
import { action, atom, computed, effect } from "@reatom/core"
import { createScope, molecule } from "bunshi"
import {
  type AvailableStatisticPeriod,
  DEFAULT_FILTER,
  DEFAULT_SORT,
  INITIAL_FILTERS,
  resolveStatisticPeriod,
} from "./default-values"
import {
  type QueryInput,
  type QueryResponse,
  getCollectionLeaderboardQueryParams,
  getUrlSearchParams,
} from "./utils"

export interface Filters {
  size: number
  statisticPeriod: AvailableStatisticPeriod
  blockchains: Blockchain[]
}

export type LeaderboardItem = QueryResponse[number]

export interface ScopeState {
  pageType?: LeaderboardPageType
  filters: Filters
  sort: {
    field: CollectionSearchSort
    direction: CollectionSearchSortDirection
  }
}

export const CollectionLeaderboardScope = createScope<ScopeState>({
  filters: {
    size: 100,
    statisticPeriod: DEFAULT_FILTER,
    blockchains: [],
  },
  sort: DEFAULT_SORT,
})

export const CollectionLeaderboardFiltersMoleculeReatom = molecule(
  (mol, scope) => {
    const { filters: initialFilters, sort: initialSort } = scope(
      CollectionLeaderboardScope,
    )

    const { selectedBlockchain, blockchainsQueryFilter } = mol(
      BlockchainFiltersMoleculeReatom,
    )

    // Atomization: separate atoms for each filter
    // Size is computed to adjust based on blockchain selection
    // "ALL" blockchain shows fewer items (15) due to aggregation across chains
    // Use INITIAL_FILTERS.size constant instead of scope's initialFilters.size
    // to avoid stale values during client-side navigation
    const size = computed(() => {
      const blockchain = selectedBlockchain()
      return blockchain === "ALL" ? 15 : INITIAL_FILTERS.size
    }, "leaderboardSize")
    // The period last picked. Switching chains happens client-side, so it can
    // land on a chain that does not offer it (1h, 6h or 30d on Solana, 1h or
    // 6h on Robinhood): the table then shows the chain's default period
    // without forgetting the pick, and shows the pick again on a chain that
    // offers it.
    const pickedStatisticPeriod = atom(
      initialFilters.statisticPeriod,
      "leaderboardPickedStatisticPeriod",
    )
    const statisticPeriod = computed(
      () =>
        resolveStatisticPeriod(selectedBlockchain(), pickedStatisticPeriod()),
      "leaderboardStatisticPeriod",
    )
    const owner = atom<UnionAddress | undefined>(undefined, "leaderboardOwner")
    const sortBy = atom(initialSort.field, "leaderboardSortBy")
    const sortDirection = atom(
      initialSort.direction,
      "leaderboardSortDirection",
    )

    // Computed blockchains based on filter selection
    // IMPORTANT: Use blockchainsQueryFilter which respects global wallet restrictions
    const blockchains = computed(() => {
      const blockchain = selectedBlockchain()
      return blockchain === "ALL" ? blockchainsQueryFilter() : [blockchain]
    }, "leaderboardBlockchains")

    // Actions
    const toggleSortDirection = action(() => {
      sortDirection.set((direction) =>
        direction === CollectionSearchSortDirection.ASC
          ? CollectionSearchSortDirection.DESC
          : CollectionSearchSortDirection.ASC,
      )
    }, "toggleSortDirection")

    const toggleSortBy = action((newSortBy: CollectionSearchSort) => {
      const currentSort = sortBy()
      if (currentSort === newSortBy) {
        toggleSortDirection()
      } else {
        sortBy.set(newSortBy)
        sortDirection.set(DEFAULT_SORT.direction)
      }
    }, "toggleSortBy")

    // Note: URL persistence moved to useEffect in CollectionLeaderboardContent component
    // to avoid "Cannot update component while rendering" React error

    return {
      size,
      statisticPeriod,
      pickedStatisticPeriod,
      blockchains,
      owner,
      sortBy,
      sortDirection,
      toggleSortBy,
    }
  },
)

export const CollectionLeaderboardMoleculeReatom = molecule((mol, scope) => {
  const bffApiClient = mol(BffApiClientMolecule)
  const { size, statisticPeriod, owner, sortBy, sortDirection, blockchains } =
    mol(CollectionLeaderboardFiltersMoleculeReatom)
  const { unionAddress: connectedWallet } = mol(WalletConnectMoleculeReatom)

  // Get pageType from scope to determine if owner filter should be applied
  const { pageType } = scope(CollectionLeaderboardScope)

  // Computed query input
  const queryInput = computed(
    (): QueryInput => ({
      size: size(),
      statisticPeriod: statisticPeriod(),
      blockchains: blockchains(),
      owner: owner(),
      sort: {
        field: sortBy(),
        direction: sortDirection(),
      },
    }),
    "leaderboardQueryInput",
  )

  // Effect: apply connected wallet as owner filter ONLY on HOLDING page
  // On TRENDING and WATCHLIST pages, this effect won't run
  if (pageType === LeaderboardPageType.HOLDING) {
    effect(() => {
      const wallet = connectedWallet()
      if (wallet) {
        owner.set(wallet)
      }
    }, "applyConnectedWalletAsOwnerOnHoldingPage")
  }

  // Query params helper for TanStack Query
  const getLeaderboardQuery = () => ({
    ...getCollectionLeaderboardQueryParams(bffApiClient, queryInput()),
    refetchInterval: 10000,
    retry: false,
  })

  return {
    queryInput,
    getLeaderboardQuery,
    priceView, // Export shared price view atom
  }
})
